import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { Modal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { IconLabel } from '@/components/ui/app-icon'
import { Input } from '@/components/ui/input'
import { useNomenclature } from '@/hooks/useNomenclature'
import { templatesApi, nomenclatureApi } from '@/lib/api'
import type { NomenclatureItem } from '@/types'

const schema = z.object({
  gtin: z.string()
    .min(13, 'GTIN минимум 13 цифр')
    .max(14, 'GTIN максимум 14 цифр')
    .regex(/^\d+$/, 'GTIN должен содержать только цифры'),
  name: z.string().min(1, 'Название обязательно'),
  brand: z.string(),
  product_code: z.string(),
  description: z.string(),
  km_template: z.string(),
  ka_box_template: z.string(),
  ka_pallet_template: z.string(),
})

type FormData = z.infer<typeof schema>

interface TemplateOption {
  filename: string
  name: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  editItem: NomenclatureItem | null
}

export function NomenclatureModal({ isOpen, onClose, editItem }: Props) {
  const { create, update, isCreating, isUpdating } = useNomenclature()
  const isEdit = !!editItem
  const [templates, setTemplates] = useState<TemplateOption[]>([])
  const [customFields, setCustomFields] = useState<Array<{ key: string; value: string }>>([])

  // Standard fields that should NOT show in the dynamic section
  const STANDARD_KEYS = new Set([
    'gtin', 'name', 'brand', 'product_code', 'description',
    'km_template', 'ka_box_template', 'ka_pallet_template',
    'created_at', 'updated_at', 'unit',
  ])

  // Fetch known dynamic field names for autosuggestions
  const { data: knownDynamicFields } = useQuery({
    queryKey: ['nomenclature-dynamic-fields'],
    queryFn: async () => (await nomenclatureApi.getDynamicFields()).data,
    staleTime: 60_000,
    enabled: isOpen,
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      gtin: '',
      name: '',
      brand: '',
      product_code: '',
      description: '',
      km_template: '',
      ka_box_template: '',
      ka_pallet_template: '',
    },
  })

  // Load templates when modal opens
  useEffect(() => {
    if (isOpen) {
      templatesApi.getAll().then(res => {
        const data = res.data as TemplateOption[]
        setTemplates(data ?? [])
      }).catch(() => setTemplates([]))
    }
  }, [isOpen])

  // Reset form when modal opens/editItem changes
  useEffect(() => {
    if (isOpen) {
      reset(
        editItem
          ? {
              gtin: editItem.gtin,
              name: editItem.name,
              brand: editItem.brand || '',
              product_code: editItem.product_code || '',
              description: editItem.description || '',
              km_template: editItem.km_template || '',
              ka_box_template: editItem.ka_box_template || '',
              ka_pallet_template: editItem.ka_pallet_template || '',
            }
          : { gtin: '', name: '', brand: '', product_code: '', description: '', km_template: '', ka_box_template: '', ka_pallet_template: '' },
      )
      // Extract custom fields from editItem (backend flattens them into root)
      if (editItem) {
        const custom: Array<{ key: string; value: string }> = []
        for (const [k, v] of Object.entries(editItem)) {
          if (!STANDARD_KEYS.has(k) && typeof v === 'string') {
            custom.push({ key: k, value: v })
          }
        }
        setCustomFields(custom)
      } else {
        setCustomFields([])
      }
    }
  }, [isOpen, editItem, reset])

  const onSubmit = (data: FormData) => {
    const templateFields = {
      km_template: data.km_template || '',
      ka_box_template: data.ka_box_template || '',
      ka_pallet_template: data.ka_pallet_template || '',
    }
    // Build extra_fields from custom key-value pairs
    const extraFields: Record<string, string> = {}
    for (const cf of customFields) {
      const k = cf.key.trim()
      if (k) extraFields[k] = cf.value
    }
    if (isEdit) {
      update(
        {
          gtin: data.gtin,
          data: {
            name: data.name,
            brand: data.brand,
            product_code: data.product_code,
            description: data.description,
            ...templateFields,
            ...extraFields,
          },
        },
        { onSuccess: () => onClose() },
      )
    } else {
      create({ ...data, ...templateFields, ...extraFields }, { onSuccess: () => onClose() })
    }
  }

  // All templates for the dropdowns
  const allTemplates = templates

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header onClose={onClose}>
        {isEdit ? 'Редактировать товар' : 'Добавить товар'}
      </Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label htmlFor="gtin" className="block text-sm font-medium text-gray-700 mb-1">
                GTIN <span className="text-red-500">*</span>
              </label>
              <Input id="gtin" placeholder="4606789012345" disabled={isEdit} {...register('gtin')} error={errors.gtin?.message} />
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Название <span className="text-red-500">*</span>
              </label>
              <Input id="name" placeholder="Название товара" {...register('name')} error={errors.name?.message} />
            </div>

            <div>
              <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-1">Бренд</label>
              <Input id="brand" placeholder="Бренд" {...register('brand')} />
            </div>

            <div>
              <label htmlFor="product_code" className="block text-sm font-medium text-gray-700 mb-1">Артикул</label>
              <Input id="product_code" placeholder="Артикул" {...register('product_code')} />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
              <Input id="description" placeholder="Описание" {...register('description')} />
            </div>

            {/* ── Шаблоны этикеток ── */}
            <div className="pt-2 border-t border-gray-200">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                <IconLabel icon="labels" iconClassName="h-4 w-4">Шаблоны этикеток (приоритетные)</IconLabel>
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Если задан — используется для этой номенклатуры вместо шаблона по умолчанию из настроек.
              </p>

              <div className="space-y-3">
                <div>
                  <label htmlFor="km_template" className="block text-xs font-medium text-gray-600 mb-1">
                    Шаблон КМ этикетки
                  </label>
                  <select
                    id="km_template"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...register('km_template')}
                  >
                    <option value="">— по умолчанию из настроек —</option>
                    {allTemplates.map(t => (
                      <option key={t.filename} value={t.filename}>{t.name} ({t.filename})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="ka_box_template" className="block text-xs font-medium text-gray-600 mb-1">
                    Шаблон КА коробки
                  </label>
                  <select
                    id="ka_box_template"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...register('ka_box_template')}
                  >
                    <option value="">— по умолчанию из настроек —</option>
                    {allTemplates.map(t => (
                      <option key={t.filename} value={t.filename}>{t.name} ({t.filename})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="ka_pallet_template" className="block text-xs font-medium text-gray-600 mb-1">
                    Шаблон КА паллеты
                  </label>
                  <select
                    id="ka_pallet_template"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...register('ka_pallet_template')}
                  >
                    <option value="">— по умолчанию из настроек —</option>
                    {allTemplates.map(t => (
                      <option key={t.filename} value={t.filename}>{t.name} ({t.filename})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* ── Дополнительные поля (динамические) ── */}
            <div className="pt-2 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700">
                  <IconLabel icon="fields" iconClassName="h-4 w-4">Дополнительные поля</IconLabel>
                </p>
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                  onClick={() => setCustomFields(prev => [...prev, { key: '', value: '' }])}
                >
                  <Plus className="h-3 w-3" /> Добавить поле
                </button>
              </div>
              {customFields.length === 0 ? (
                <p className="text-xs text-gray-400">Нет дополнительных полей. Нажмите «Добавить поле».</p>
              ) : (
                <div className="space-y-2">
                  {customFields.map((cf, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Название поля"
                        value={cf.key}
                        list="dynamic-field-hints"
                        onChange={e => {
                          const next = [...customFields]
                          next[idx] = { ...next[idx], key: e.target.value }
                          setCustomFields(next)
                        }}
                      />
                      <input
                        type="text"
                        className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Значение"
                        value={cf.value}
                        onChange={e => {
                          const next = [...customFields]
                          next[idx] = { ...next[idx], value: e.target.value }
                          setCustomFields(next)
                        }}
                      />
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        onClick={() => setCustomFields(prev => prev.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {/* Datalist for autocomplete suggestions from known dynamic fields */}
                  <datalist id="dynamic-field-hints">
                    {(knownDynamicFields ?? []).map(f => (
                      <option key={f.key} value={f.key} />
                    ))}
                  </datalist>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onClose}>Отмена</Button>
          <Button type="submit" isLoading={isCreating || isUpdating}>
            {isEdit ? 'Обновить' : 'Создать'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
